export function readCookie(name) {
    if (typeof document === 'undefined')
        return null;
    const cookie = document.cookie;
    if (!cookie)
        return null;
    const parts = cookie.split(';');
    let value = null;
    for (const part of parts) {
        const [key, ...rest] = part.split('=');
        if (key?.trim() === name) {
            const rawValue = rest.join('=');
            try {
                const decodedValue = decodeURIComponent(rawValue);
                if (decodedValue.trim()) {
                    value = decodedValue;
                }
            }
            catch {
                // A malformed duplicate must not hide a later fresh CSRF cookie.
            }
        }
    }
    return value;
}
export const INVALID_CSRF_MESSAGE = 'Invalid CSRF token';
export function isExactInvalidCsrfResponse(status, body) {
    if (status !== 403 || typeof body !== 'object' || body === null) {
        return false;
    }
    const message = body['message'];
    if (typeof message === 'string') {
        return message === INVALID_CSRF_MESSAGE;
    }
    return (Array.isArray(message) &&
        message.length === 1 &&
        typeof message[0] === 'string' &&
        message[0] === INVALID_CSRF_MESSAGE);
}
async function responseHasExactInvalidCsrfSignal(response) {
    if (response.status !== 403) {
        return false;
    }
    try {
        return isExactInvalidCsrfResponse(response.status, await response.clone().json());
    }
    catch {
        return false;
    }
}
/**
 * Replays one cookie-auth request after the IDP's exact stale-CSRF response.
 *
 * Callers must supply a refresh function that obtains the replacement token
 * from the CSRF response header before falling back to the browser cookie.
 * This intentionally never retries generic 401/403 responses.
 */
export async function fetchAuthWithCsrfRetry(input, initFactory, options) {
    const initialToken = options.initialXsrfToken ?? undefined;
    const response = await fetch(input, await initFactory(initialToken));
    if (!(await responseHasExactInvalidCsrfSignal(response))) {
        return response;
    }
    const refreshedToken = (await options.refreshCsrfToken()) ?? undefined;
    if (!refreshedToken) {
        return response;
    }
    return fetch(input, await initFactory(refreshedToken));
}
/**
 * Obtains a browser CSRF token, preferring the response header so a stale
 * visible cookie cannot win over the fresh token just issued by the IDP.
 */
export async function primeAuthCsrf(path = '/api/auth/csrf') {
    try {
        const response = await fetch(path, {
            cache: 'no-store',
            credentials: 'include',
            method: 'GET',
        });
        return (readResponseHeader(response.headers, 'x-xsrf-token') ?? readCookie('XSRF-TOKEN') ?? undefined);
    }
    catch {
        return undefined;
    }
}
export function readResponseHeader(headers, name) {
    if (!headers)
        return null;
    const lower = name.toLowerCase();
    if (typeof headers.get === 'function') {
        const value = headers.get(name);
        return typeof value === 'string' && value.trim() ? value.trim() : null;
    }
    const record = headers;
    const value = record[name] ??
        record[lower] ??
        Object.entries(record).find(([key]) => key.toLowerCase() === lower)?.[1];
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}
//# sourceMappingURL=csrf-token.js.map